import { useEffect, useMemo, useState } from 'react';
import { Link, matchPath, useLocation } from 'react-router';
import {
  LayoutDashboard,
  Users,
  Activity,
  BarChart3,
  Download,
  X,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import { runtimeConfig } from '@/config/runtime';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  label: string;
  href: string;
  icon: typeof LayoutDashboard;
}

const doctorNavItems: NavItem[] = [
  { label: 'Dashboard', href: '/doctor', icon: LayoutDashboard },
  { label: 'Patients', href: '/doctor/patients', icon: Users },
  { label: 'Activities Download', href: '/doctor/download', icon: Download },
  { label: 'Activities Raw Download', href: '/doctor/download-raw', icon: Download },
  { label: 'RTP Download', href: '/doctor/rtp-download', icon: BarChart3 },
];

const patientNavItems: NavItem[] = [
  { label: 'Dashboard', href: '/patient', icon: LayoutDashboard },
  { label: 'Activities Download', href: '/patient/activities', icon: Activity },
  { label: 'Activities Raw Download', href: '/patient/download-raw', icon: Download },
  { label: 'RTP Download', href: '/patient/rtp-download', icon: Download },
];

function findActiveHref(pathname: string, navItems: NavItem[]): string | null {
  return (
    navItems.find((item) =>
      matchPath(
        {
          path: item.href,
          end: item.href === '/doctor' || item.href === '/patient',
        },
        pathname
      )
    )?.href ?? null
  );
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { user } = useAuth();
  const location = useLocation();
  const navItems = useMemo(
    () =>
      (user?.role === 'doctor' ? doctorNavItems : patientNavItems).filter(
        (item) => runtimeConfig.rawDataDownloadEnabled || !item.href.endsWith('/download-raw')
      ),
    [user?.role]
  );
  const [activeHref, setActiveHref] = useState(() =>
    findActiveHref(location.pathname, navItems)
  );

  useEffect(() => {
    const routeActiveHref = findActiveHref(location.pathname, navItems);
    if (routeActiveHref) {
      setActiveHref(routeActiveHref);
    }
  }, [location.pathname, navItems]);

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-50 h-full w-64 bg-white shadow-lg transition-transform lg:static lg:z-0 lg:shadow-none lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-16 items-center justify-between border-b border-border px-4 lg:hidden">
          <span className="font-semibold text-foreground">Menu</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <nav className="flex flex-col gap-1 p-4">
          {navItems.map((item) => {
            const isActive = activeHref === item.href;

            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => {
                  setActiveHref(item.href);
                  onClose();
                }}
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary-50 text-primary'
                    : 'text-muted hover:bg-gray-100 hover:text-foreground'
                )}
              >
                <item.icon className="h-5 w-5" />
                {item.label}
              </Link>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
